





















































































import { ClientAccountsApi } from '@/api/client-accounts';
import { ManageDuplicateApi } from '@/api/manage-duplicate';
import { MergeApi } from '@/api/merge';
import { ClientMergeRequest } from '@/api/requests/merge/client-merge-request';
import clientAccountSummary from '@/components/data-management/duplicate-clients/client-account-summary.vue';
import clientDuplicateTable from '@/components/data-management/duplicate-clients/client-duplicate-table.vue';
import clientMergeConflicts from '@/components/data-management/duplicate-clients/client-merge-conflicts.vue';
import { findMergeConflicts, findPriorityClientAccount } from '@/components/data-management/duplicate-clients/duplicate-client-utilities';
import linkPartnerTable from '@/components/data-management/duplicate-clients/link-partner-table.vue';
import loading from '@/components/loading.vue';
import { ClientAccount } from '@/shared/clients/client-account';
import { ClientAccountDuplicate } from '@/shared/clients/client-account-duplicate';
import { ClientMergeConflict } from '@/shared/data-management/client-merge-conflict';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

const clientApi = new ClientAccountsApi();
const manageDuplicateApi = new ManageDuplicateApi();
const mergeApi = new MergeApi();

@Component({
    components: {
        clientAccountSummary,
        clientDuplicateTable,
        clientMergeConflicts,
        linkPartnerTable,
        loading,
    },
})
export default class ClientMergeDialog extends Vue {
    @Prop({ required: true }) private visible!: boolean;
    @Prop({ required: false, default: null }) private client!: ClientAccount | null;
    @Prop({ required: false, default: false }) private manualMerge!: boolean;

    private isVisible: boolean = this.visible;
    private isManualMerge: boolean = this.manualMerge;
    private isLoading: boolean = true;
    private isMerging: boolean = false;
    private currentStep: number = 0;
    private clientsCompleted: boolean = false;
    private linkPartnersCompleted: boolean = false;
    private manualMergeErrorMessage: string | null = null;
    private clientDuplicates: Array<ClientAccountDuplicate & { selected: boolean }> = new Array<ClientAccountDuplicate & { selected: boolean }>();
    private clientPartners: Array<ClientAccountDuplicate & { selected: boolean }> = new Array<ClientAccountDuplicate & { selected: boolean }>();
    private clientAccountOne: ClientAccount | null = null;
    private clientAccountTwo: ClientAccount | null = null;
    private clientPartner: ClientAccount | null = null;
    private priorityClientAccount: ClientAccount | null = null;
    private mergeClientAccount: ClientAccount | null = null;
    private mergeConflicts: Array<ClientMergeConflict> = new Array<ClientMergeConflict>();
    private mergeLog: Array<string> = new Array<string>();

    private get maxSteps(): number {
        return this.mergeConflicts.length ? 3 : 2;
    }

    private get showPrevious(): boolean {
        if (this.currentStep === 1 && (this.clientDuplicates.length || this.isManualMerge)) {
            return true;
        }

        if (this.currentStep === 2 && this.clientPartner == null && this.clientPartners.length) {
            return true;
        }

        return false;
    }

    private get isNextDisabled(): boolean {
        let currentStep = this.currentStep;

        if (currentStep > 0 && !this.mergeConflicts.length) {
            currentStep++;
        }

        switch (currentStep) {
            case 0: // Clients
                return this.isManualMerge ? this.manualMergeInvalid : false;

            case 1: // Merge Client
                return this.mergeConflicts.length ? this.mergeConflicts.some((c) => c.useMergeValue == null) : false;

            default:
                return false;
        }
    }

    private get nextTitle(): string {
        let currentStep = this.currentStep;

        if (currentStep > 0 && !this.mergeConflicts.length) {
            currentStep++;
        }

        switch (currentStep) {
            case 0: // Clients
                return this.clientAccountOne != null && this.clientAccountTwo != null ? 'Merge' : 'Next';
            case 1: // Merge Client
                return 'Merge';
            case 2: // Link Partners
                return this.clientAccountOne != null && this.clientPartner != null ? 'Link' : 'Next';
            default:
                return 'Next';
        }
    }

    private get manualMergeInvalid(): boolean {
        this.manualMergeErrorMessage = null;

        if (this.clientAccountOne == null || this.clientAccountTwo == null) {
            return true;
        }

        if (this.clientAccountOne!.id! === this.clientAccountTwo!.id!) {
            this.manualMergeErrorMessage = `'Client Account 1' and 'Client Account 2' cannot be the same.`;
            return true;
        }

        if (this.clientAccountOne!.partner != null && this.clientAccountOne.partner.id! === this.clientAccountTwo!.id!) {
            this.manualMergeErrorMessage = `Client Account 1' and 'Client Account 2' cannot be linked.`;
            return true;
        }

        if (this.clientAccountOne!.partnerPracticeId! !== '00000000-0000-0000-0000-000000000000' &&
            this.clientAccountTwo!.partnerPracticeId! !== '00000000-0000-0000-0000-000000000000' &&
            this.clientAccountOne.partnerPracticeId !== this.clientAccountTwo.partnerPracticeId) {
            this.manualMergeErrorMessage = `'Client Account 1' and 'Client Account 2' must be assigned to the same partner practice.`;
            return true;
        }

        return false;
    }

    @Watch('visible')
    private onVisibleChanged(visible: boolean): void {
        this.$validator.errors.clear();
        this.isVisible = visible;
    }

    @Watch('manualMerge')
    private onManualMergeChanged(manualMerge: boolean): void {
        this.isManualMerge = manualMerge;
    }

    private async onNextAsync(): Promise<void> {
        let currentStep: number = this.currentStep;

        if (currentStep > 0 && !this.mergeConflicts.length) {
            currentStep++;
        }

        switch (currentStep) {
            case 0: // Clients
                if (this.isManualMerge && (!await (this.$refs.clientAccountOne as Vue).$validator.validateAll('client-details') || !await (this.$refs.clientAccountTwo as Vue).$validator.validateAll('client-details'))) {
                    return;
                }

                this.clientsCompleted = true;

                if (this.clientAccountOne == null || this.clientAccountTwo == null) {
                    break;
                }

                if (!this.mergeConflicts.length) {
                    await this.onMergeAsync();
                    return;
                }

                break;

            case 1: // Merge Client
                if (!(await (this.$refs.clientMergeConflicts as Vue).$validator.validateAll('merge-conflicts'))) {
                    return;
                }

                await this.onMergeAsync();
                return;

            case 2: // Link Partners
                this.linkPartnersCompleted = true;

                if (this.clientAccountOne == null || this.clientPartner == null) {
                    break;
                }

                await this.onLinkAsync();
                break;
        }

        setTimeout(() => { this.currentStep++; }, 0);
    }

    private async onMergeAsync(): Promise<void> {
        this.isMerging = true;

        try {
            const request: ClientMergeRequest = {
                priorityClientId: this.priorityClientAccount!.id!,
                mergeClientId: this.mergeClientAccount!.id!,
                selectedMergeValues: this.mergeConflicts
                    .filter((conflict) => conflict.useMergeValue)
                    .map((conflict) => conflict.fieldName[0].toUpperCase() + conflict.fieldName.substring(1)),
            };

            await mergeApi.mergeClientAsync(request);
            this.$snackbar.open({ message: `Client account ${this.priorityClientAccount!.accountNo} and client account ${this.mergeClientAccount!.accountNo} merged successfully.`, type: 'is-success' });

            const priorityClientDescription: string = `${this.priorityClientAccount!.accountNo} - ${this.priorityClientAccount!.firstName!.trim()} ${this.priorityClientAccount!.lastName!.trim()}`;
            const mergeClientDescription: string = `${this.mergeClientAccount!.accountNo} - ${this.mergeClientAccount!.firstName!.trim()} ${this.mergeClientAccount!.lastName!.trim()}`;
            this.mergeLog.push(`Client account '${priorityClientDescription}' merged with account '${mergeClientDescription}'.`);


            await this.resetAfterMergeAsync();
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: 'Could not merge clients, please try again.', type: 'is-danger' });
            }
        }

        this.isMerging = false;
    }

    private async onLinkAsync(): Promise<void> {
        this.isMerging = true;

        try {
            await clientApi.assignClientPartnerAsync({ ...this.clientAccountOne! }, this.clientPartner!.id!);
            this.$snackbar.open({ message: `Client account ${this.clientAccountOne!.accountNo} and client account ${this.clientPartner!.accountNo} linked successfully.`, type: 'is-success' });

            const clientDescription: string = `${this.clientAccountOne!.accountNo} - ${this.clientAccountOne!.firstName!.trim()} ${this.clientAccountOne!.lastName!.trim()}`;
            const partnerDescription: string = `${this.clientPartner!.accountNo} - ${this.clientPartner!.firstName!.trim()} ${this.clientPartner!.lastName!.trim()}`;
            this.mergeLog.push(`Client account '${clientDescription}' linked with account '${partnerDescription}'.`);
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: 'Could not link clients, please try again.', type: 'is-danger' });
            }
        }

        this.isMerging = false;
    }

    private async onCompleteAsync(): Promise<void> {
        this.isMerging = true;

        try {
            await mergeApi.mergeCompleteAsync(this.clientAccountOne!.id!);
            this.$emit('cancel');
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: 'Could not complete client merge, please try again.', type: 'is-danger' });
            }
        }

        this.isMerging = false;
    }

    private onPrevious(): void {
        this.currentStep--;
    }

    private onCancel(): void {
        this.$emit('cancel');
    }

    private async fetchPotentialDuplicatesAsync(clientId: string): Promise<void> {
        this.clientAccountOne = await clientApi.getClientAccountByIdAsync(clientId);

        const clientDuplicates = await manageDuplicateApi.getDuplicateClientsByIdAsync(clientId, { excludeLinkedClients: false });

        this.clientDuplicates = clientDuplicates.length
            ? clientDuplicates
                .map((client) => this.createClientDuplicate(client))
                .sort((c1, c2) => {
                    if (c1.matchPercent! < c2.matchPercent!) { return 1; }
                    if (c1.matchPercent! > c2.matchPercent!) { return -1; }
                    return 0;
                })
            : [];

        if (this.clientAccountOne.partner != null) {
            this.clientPartners = [];
            return;
        }

        const clientPartners = await manageDuplicateApi.getDuplicateClientsByIdAsync(clientId, { excludeLinkedClients: true });

        this.clientPartners = clientPartners.length
            ? clientPartners.map((client) => this.createClientDuplicate(client))
            : [];
    }

    private async resetAfterMergeAsync(): Promise<void> {
        const priorityClientAccountId = this.priorityClientAccount!.id!;

        this.clientAccountOne = null;
        this.clientAccountTwo = null;
        this.priorityClientAccount = null;
        this.mergeClientAccount = null;
        this.mergeConflicts = [];

        this.isManualMerge = false;
        this.currentStep = 0;

        this.isLoading = true;
        await this.fetchPotentialDuplicatesAsync(priorityClientAccountId);
        this.isLoading = false;

        if (!this.clientDuplicates.length) {
            await this.onNextAsync();
        }
    }

    private createClientDuplicate(client: ClientAccountDuplicate): ClientAccountDuplicate & { selected: boolean } {
        return {
            ...client,
            partner: client.partner == null ? null : { ...client.partner },
            factFind: client.factFind == null ? null : { ...client.factFind },
            will: client.will == null ? null : { ...client.will },
            powerOfAttorneys: [...client.powerOfAttorneys],
            deeds: [...client.deeds],
            selected: false,
        };
    }

    private async onClientAccountOneChangedAsync(client: ClientAccount | null): Promise<void> {
        this.clientAccountOne = client == null ? null : await clientApi.getClientAccountByIdAsync(client.id!);
        this.resetPriorityAndConflicts();
    }

    private async onClientAccountTwoChangedAsync(client: ClientAccount | null): Promise<void> {
        this.clientAccountTwo = client == null ? null : await clientApi.getClientAccountByIdAsync(client.id!);
        this.resetPriorityAndConflicts();
    }

    private async onClientPartnerChangedAsync(client: ClientAccount | null): Promise<void> {
        this.clientPartner = client;
    }

    private resetPriorityAndConflicts(): void {
        this.priorityClientAccount = null;
        this.mergeClientAccount = null;
        this.mergeConflicts = [];

        if (this.clientAccountOne == null || this.clientAccountTwo == null) {
            return;
        }

        const priorityClientAccountId = findPriorityClientAccount(this.clientAccountOne, this.clientAccountTwo);

        this.priorityClientAccount = this.clientAccountOne.id! === priorityClientAccountId ? this.clientAccountOne : this.clientAccountTwo;
        this.mergeClientAccount = this.clientAccountOne.id! === priorityClientAccountId ? this.clientAccountTwo : this.clientAccountOne;

        this.mergeConflicts = findMergeConflicts(this.priorityClientAccount, this.mergeClientAccount);
    }

    private async mounted(): Promise<void> {
        if (this.client == null) {
            this.isLoading = false;
            return;
        }

        this.isLoading = true;
        await this.fetchPotentialDuplicatesAsync(this.client.id!);
        this.isLoading = false;
    }
}
